/**
 * Offline Status Indicator Component
 * Shows connection status and sync information
 */

import React, { useState, useEffect } from 'react';
import { offlineStorageService } from '@/services/offlineStorageService';
import { offlineSyncService, SyncStatus } from '@/services/offlineSyncService';
import { useAuth } from '@/context/AuthContext';
import {
  Wifi,
  WifiOff,
  Cloud,
  CloudOff,
  RotateCw,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';

interface OfflineStatusIndicatorProps {
  showDetails?: boolean;
  compact?: boolean;
  className?: string;
  navigationMode?: boolean; // New prop for navigation sidebar display
}

export default function OfflineStatusIndicator({
  showDetails = false,
  compact = false,
  className = '',
  navigationMode = false
}: OfflineStatusIndicatorProps) {
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(offlineStorageService.isOnline());
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isRunning: false,
    progress: 100,
    pendingItems: 0
  });
  const [storageUsage, setStorageUsage] = useState({ used: 0, quota: 0 });

  useEffect(() => {
    const cleanup = offlineStorageService.onConnectionChange(setIsOnline);
    return cleanup;
  }, []);

  useEffect(() => {
    const cleanup = offlineSyncService.onSyncStatusChange(setSyncStatus);
    return cleanup;
  }, []);

  useEffect(() => {
    const updateStorageUsage = async () => {
      const usage = await offlineStorageService.getStorageUsage();
      setStorageUsage(usage);
    };

    updateStorageUsage();
    const interval = setInterval(updateStorageUsage, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const handleManualSync = async () => {
    if (!user || !isOnline) return;

    try {
      await offlineSyncService.forceSyncNow(user.uid);
    } catch (error) {
      // console.error('Manual sync failed:', error);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = () => {
    if (!isOnline) return 'text-red-500';
    if (syncStatus.isRunning) return 'text-blue-500';
    if (syncStatus.pendingItems > 0) return 'text-orange-500';
    return 'text-green-500';
  };

  const getStatusIcon = () => {
    if (!isOnline) return <WifiOff className="w-4 h-4" />;
    if (syncStatus.isRunning) return <RotateCw className="w-4 h-4 animate-spin" />;
    if (syncStatus.pendingItems > 0) return <CloudOff className="w-4 h-4" />;
    return <Cloud className="w-4 h-4" />;
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (syncStatus.isRunning) return 'Syncing...';
    if (syncStatus.pendingItems > 0) return `${syncStatus.pendingItems} pending`;
    return 'Synced';
  };

  const getTooltipDescription = () => {
    if (!isOnline) {
      return 'Working offline. Your data will sync automatically when connection is restored.';
    }
    if (syncStatus.isRunning) {
      return `Syncing your workout data to the cloud... ${syncStatus.progress}% complete`;
    }
    if (syncStatus.pendingItems > 0) {
      return `${syncStatus.pendingItems} items waiting to sync. Click to sync now.`;
    }
    return 'All your workout data is safely synced to the cloud.';
  };

  if (compact) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className={getStatusColor()}>
          {getStatusIcon()}
        </div>
        <span className={`text-sm ${getStatusColor()}`}>
          {getStatusText()}
        </span>
      </div>
    );
  }

  // Navigation mode - compact display with tooltip
  if (navigationMode) {
    return (
      <div
        className={`group relative bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-colors cursor-help ${className}`}
        title={getTooltipDescription()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={getStatusColor()}>
              {getStatusIcon()}
            </div>
            <span className={`text-sm font-medium ${getStatusColor()}`}>
              {getStatusText()}
            </span>
          </div>

          {syncStatus.pendingItems > 0 && isOnline && (
            <button
              onClick={handleManualSync}
              disabled={syncStatus.isRunning}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 ml-2"
            >
              Sync
            </button>
          )}
        </div>

        {/* Tooltip */}
        <div className="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
          {getTooltipDescription()}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className={getStatusColor()}>
            {getStatusIcon()}
          </div>
          <span className={`font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>

        {syncStatus.pendingItems > 0 && isOnline && (
          <button
            onClick={handleManualSync}
            disabled={syncStatus.isRunning}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            Sync Now
          </button>
        )}
      </div>

      {showDetails && (
        <div className="space-y-2 text-sm text-gray-600">
          {/* Connection Status */}
          <div className="flex items-center justify-between">
            <span>Connection:</span>
            <div className="flex items-center space-x-1">
              {isOnline ? (
                <>
                  <Wifi className="w-3 h-3 text-green-500" />
                  <span className="text-green-600">Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 text-red-500" />
                  <span className="text-red-600">Offline</span>
                </>
              )}
            </div>
          </div>

          {/* Sync Progress */}
          {syncStatus.isRunning && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span>Sync Progress:</span>
                <span>{syncStatus.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${syncStatus.progress}%` }}
                />
              </div>
              {syncStatus.currentItem && (
                <div className="text-xs text-gray-500">
                  {syncStatus.currentItem}
                </div>
              )}
            </div>
          )}

          {/* Pending Items */}
          {syncStatus.pendingItems > 0 && (
            <div className="flex items-center justify-between">
              <span>Pending Sync:</span>
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3 text-orange-500" />
                <span className="text-orange-600">
                  {syncStatus.pendingItems} items
                </span>
              </div>
            </div>
          )}

          {/* Last Sync */}
          {syncStatus.lastSync && (
            <div className="flex items-center justify-between">
              <span>Last Sync:</span>
              <span>{syncStatus.lastSync.toLocaleTimeString()}</span>
            </div>
          )}

          {/* Storage Usage */}
          {storageUsage.quota > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span>Storage:</span>
                <span>
                  {formatBytes(storageUsage.used)} / {formatBytes(storageUsage.quota)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div
                  className="bg-gray-600 h-1 rounded-full"
                  style={{ 
                    width: `${Math.min((storageUsage.used / storageUsage.quota) * 100, 100)}%` 
                  }}
                />
              </div>
            </div>
          )}

          {/* Status Messages */}
          {!isOnline && (
            <div className="flex items-start space-x-2 p-2 bg-red-50 rounded-md">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-red-700">
                <div className="font-medium">Working Offline</div>
                <div className="text-xs">
                  Your data will sync automatically when connection is restored.
                </div>
              </div>
            </div>
          )}

          {isOnline && syncStatus.pendingItems === 0 && (
            <div className="flex items-start space-x-2 p-2 bg-green-50 rounded-md">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="text-green-700">
                <div className="font-medium">All Data Synced</div>
                <div className="text-xs">
                  Your workout data is safely backed up to the cloud.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
